import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Plus, Trash2, Edit3, X, Search, Users, Star, Phone, Mail } from 'lucide-react';

function SupplierForm({ supplier, onSave, onCancel }) {
  const [form, setForm] = useState(supplier || { name: '', nif: '', contact_person: '', phone: '', email: '', address: '', category: 'geral', notes: '', rating: 0 });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">{supplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}</h1>
        <button onClick={onCancel} className="btn btn-outline btn-sm"><X size={16} /></button>
      </div>
      <div className="pro-card-padded">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="field-label">Nome/Empresa</label><input data-testid="supplier-name" className="input" value={form.name} onChange={e => set('name', e.target.value)} /></div>
          <div><label className="field-label">NIF</label><input className="input" value={form.nif} onChange={e => set('nif', e.target.value)} /></div>
          <div><label className="field-label">Pessoa de Contacto</label><input className="input" value={form.contact_person} onChange={e => set('contact_person', e.target.value)} /></div>
          <div><label className="field-label">Telefone</label><input className="input" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
          <div><label className="field-label">Email</label><input className="input" value={form.email} onChange={e => set('email', e.target.value)} /></div>
          <div><label className="field-label">Categoria</label>
            <select className="select" value={form.category} onChange={e => set('category', e.target.value)}>
              <option value="geral">Geral</option><option value="materiais">Materiais</option><option value="equipamentos">Equipamentos</option><option value="servicos">Servicos</option><option value="subempreiteiro">Subempreiteiro</option><option value="transportes">Transportes</option>
            </select></div>
          <div className="md:col-span-2"><label className="field-label">Morada</label><input className="input" value={form.address} onChange={e => set('address', e.target.value)} /></div>
          <div><label className="field-label">Avaliacao (1-5)</label><input className="input" type="number" min="0" max="5" value={form.rating} onChange={e => set('rating', parseInt(e.target.value) || 0)} /></div>
          <div><label className="field-label">Notas</label><input className="input" value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onCancel} className="btn btn-outline">Cancelar</button>
          <button data-testid="save-supplier-btn" onClick={() => onSave(form)} className="btn btn-primary">Guardar</button>
        </div>
      </div>
    </div>
  );
}

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');

  const load = useCallback(() => { api.get('/suppliers').then(r => setSuppliers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (editing) await api.put(`/suppliers/${editing.id}`, form);
    else await api.post('/suppliers', form);
    setEditing(null); setCreating(false); load();
  };

  const handleDelete = async (id) => { if (window.confirm('Eliminar?')) { await api.delete(`/suppliers/${id}`); load(); } };

  const filtered = suppliers.filter(s => {
    if (filterCat && s.category !== filterCat) return false;
    if (search && ![s.name, s.contact_person, s.email, s.phone].some(f => f?.toLowerCase().includes(search.toLowerCase()))) return false;
    return true;
  });

  if (creating || editing) return <SupplierForm supplier={editing} onSave={handleSave} onCancel={() => { setEditing(null); setCreating(false); }} />;

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold">Fornecedores</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{suppliers.length} fornecedor{suppliers.length !== 1 ? 'es' : ''}</p>
        </div>
        <button data-testid="new-supplier-btn" onClick={() => setCreating(true)} className="btn btn-primary"><Plus size={16} /> Novo Fornecedor</button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-light)' }} />
          <input data-testid="supplier-search" className="input pl-9" placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="select" style={{ width: 'auto' }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="">Todas Categorias</option>
          <option value="materiais">Materiais</option><option value="equipamentos">Equipamentos</option><option value="servicos">Servicos</option><option value="subempreiteiro">Subempreiteiro</option><option value="transportes">Transportes</option><option value="geral">Geral</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="pro-card-padded text-center py-8"><Users size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} /><p className="font-semibold">Sem fornecedores</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(s => (
            <div key={s.id} data-testid={`supplier-card-${s.id}`} className="pro-card-padded">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold truncate">{s.name}</h3>
                  {s.contact_person && <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.contact_person}</p>}
                </div>
                <span className="badge badge-neutral ml-2">{s.category}</span>
              </div>
              <div className="space-y-1 mb-3">
                {s.phone && <p className="text-xs flex items-center gap-1"><Phone size={10} /> {s.phone}</p>}
                {s.email && <p className="text-xs flex items-center gap-1"><Mail size={10} /> {s.email}</p>}
                {s.nif && <p className="text-xs" style={{ color: 'var(--text-light)' }}>NIF: {s.nif}</p>}
              </div>
              {s.rating > 0 && (
                <div className="flex items-center gap-0.5 mb-2">
                  {[1,2,3,4,5].map(i => <Star key={i} size={12} fill={i <= s.rating ? '#f59e0b' : 'none'} color={i <= s.rating ? '#f59e0b' : '#d1d5db'} />)}
                </div>
              )}
              <div className="flex gap-1">
                <button onClick={() => { setEditing(s); setCreating(true); }} className="btn btn-outline btn-xs"><Edit3 size={12} /></button>
                <button onClick={() => handleDelete(s.id)} className="btn btn-danger btn-xs"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
